// TokenUsePanel
//
// Inline panel on an expanded DetectionAlerts row that pivots the alert into
// the NON-INTERACTIVE Entra sign-in feed (graph_signin_noninteractive) — the
// feed where AiTM token replay / refresh-token reuse shows up. The interactive
// graph_signin feed never records that activity, so this is the surface that
// makes a stolen-token replay visible.
//
// Button POSTs to /api/alerts/{alertId}/token-use with { tenantId, userId,
// startUtc, hours=24 }; the backend runs four Databricks passes (token-use
// timeline, IP enrichment, correlationId stitch to the originating interactive
// sign-in, and a 30-day interactive baseline for new-for-user flags) and
// returns synchronously.
//
// Two tabs:
//   Timeline — chronological token redemptions: app / token type / IP / ASN /
//              country / result, with the AiTM tell (a SUCCESSFUL token use
//              from an ASN or country new for this user) highlighted amber.
//   Summary  — the originating interactive sign-in legs (stitched by
//              correlationId), the IPs touched with enrichment, and counts.
//
// Loading / error / empty render in-place; never throws. Array fields are
// defensively coerced (PowerShell ConvertTo-Json unwraps single-element arrays
// to a scalar) via toArr().

import React, { useState, useMemo, useEffect } from 'react';
import { ArrowPathIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import CopyButton from '../CopyButton';
import Pagination from '../Pagination';
import RawEventJsonPanel from '../sourceEvents/RawEventJsonPanel';
import { normalizeIsoUtc, resolveTz } from '../../utils/timeFormat';

function toArr(v) {
  if (Array.isArray(v)) return v;
  if (v == null || v === '') return [];
  return [v];
}

function fmtTs(ts, tzIana) {
  if (!ts) return '—';
  try {
    // resolveTz maps "Local" (empty iana) → browser zone (empty timeZone throws);
    // normalizeIsoUtc fixes zone-less / sub-ms parsing. Legacy Z shape only for an
    // actual UTC selection. See feedback-local-tz-empty-iana.
    const zone = resolveTz(tzIana);
    const d = new Date(normalizeIsoUtc(ts));
    if (zone === 'UTC') {
      return d.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, 'Z');
    }
    return d.toLocaleString('en-US', {
      timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch { return String(ts); }
}

function NewChip() {
  return (
    <span
      className="ml-1 px-1 py-0.5 rounded text-[9px] uppercase tracking-wide bg-amber-950/50 border border-amber-500/40 text-amber-300"
      title="Not seen in this user's interactive sign-ins over the last 30 days"
    >new</span>
  );
}

function ResultBadge({ result }) {
  if (result === 'success') {
    return <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-950/40 border border-emerald-500/30 text-emerald-300">success</span>;
  }
  if (result === 'failed') {
    return <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-800/60 border border-gray-700 text-gray-400">failed</span>;
  }
  return <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-800/60 border border-gray-700 text-gray-500">—</span>;
}

export default function TokenUsePanel({
  alert, tokenUse, loading, error, onRun, tzIana,
}) {
  const [tab, setTab] = useState('timeline');
  const [expanded, setExpanded] = useState({});  // eventId -> bool
  const [collapsed, setCollapsed] = useState(true);  // collapsed by default (opt-in, volume)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const sortedEvents = useMemo(() => {
    const events = toArr(tokenUse?.events);
    return [...events].sort((a, b) => {
      const ta = a?.time ? new Date(a.time).getTime() : 0;
      const tb = b?.time ? new Date(b.time).getTime() : 0;
      return ta - tb;
    });
  }, [tokenUse?.events]);

  const summary    = tokenUse?.summary || null;
  const ipSummary  = toArr(tokenUse?.ipSummary);
  const legs       = toArr(tokenUse?.interactiveLegs);

  const durationSec = tokenUse?.durationMs
    ? Math.max(1, Math.round(tokenUse.durationMs / 1000))
    : null;

  // Reset to page 1 whenever a new result loads.
  useEffect(() => { setPage(1); }, [tokenUse]);

  const totalPages = Math.max(1, Math.ceil(sortedEvents.length / pageSize));
  const pagedEvents = useMemo(
    () => sortedEvents.slice((page - 1) * pageSize, page * pageSize),
    [sortedEvents, page, pageSize]
  );

  return (
    <div className="px-6 py-3 border-b border-gray-800/60">
      {/* Section header */}
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setCollapsed(c => !c); }}
          className="flex items-center gap-1 text-gray-400 hover:text-gray-200 text-xs font-semibold tracking-wide uppercase"
        >
          {collapsed ? <ChevronRightIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />}
          Token Use — non-interactive sign-ins (Event time −2h to +24h)
          {summary && (
            <span className="ml-2 normal-case font-normal text-gray-500">
              · {summary.eventCount} token uses · {summary.distinctIpCount} IPs
              {summary.foreignAsnCount ? ` · ${summary.foreignAsnCount} from a new ASN` : ''}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRun(); }}
          disabled={loading}
          className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-blue-500/40 bg-blue-950/30 text-blue-300 hover:bg-blue-900/40 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ArrowPathIcon className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          {tokenUse ? 'Re-run' : (loading ? 'Loading…' : 'Load')}
        </button>
      </div>

      {!collapsed && (
        <>
          <p className="text-[10px] text-gray-500 mb-2 leading-relaxed">
            Non-interactive token use (refresh/PRT replay, SSO reuse) for this user around the alert time.
            A <span className="text-amber-300">successful</span> token use from an IP, ASN, or country this
            user normally doesn't sign in from can indicate a stolen refresh/PRT token being replayed against
            Microsoft Graph. <span className="text-gray-400">Note:</span> proxy-style AiTM (lookalike sign-in
            pages) bypasses the Entra sign-in logs and won't appear here — detect those on the sign-in feed.
          </p>

          {error && (
            <div className="text-[11px] px-2 py-1 rounded bg-red-950/40 border border-red-500/30 text-red-300 mb-2">
              Token use lookup failed: {error}
            </div>
          )}

          {!tokenUse && !loading && !error && (
            <p className="text-[11px] text-gray-500 italic">
              Click Load to pull this user's non-interactive sign-ins from 2h before to 24h after the alert event time.
            </p>
          )}

          {loading && !tokenUse && (
            <p className="text-[11px] text-sky-400">
              Querying the non-interactive sign-in feed (expect a few seconds)…
            </p>
          )}

          {tokenUse && (
            <>
              {/* Tab bar */}
              <div className="flex items-center gap-1 mb-2 border-b border-gray-800/60">
                {['timeline','summary'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setTab(t); }}
                    className={`px-2 py-1 text-[11px] uppercase tracking-wide border-b ${tab === t ? 'border-blue-400 text-blue-300' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
                  >{t}</button>
                ))}
                <span className="ml-auto text-[10px] text-gray-600">
                  {tokenUse.window?.startUtc} → {tokenUse.window?.endUtc}{durationSec != null ? ` · ${durationSec}s` : ''}
                  {summary && !summary.baselineKnown ? ' · no 30d baseline' : ''}
                </span>
              </div>

              {tab === 'timeline' && (
                <div className="border border-gray-800 rounded overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-gray-900/60 text-gray-400">
                      <tr>
                        <th className="w-6"></th>
                        <th className="text-left px-2 py-1 font-normal">Time</th>
                        <th className="text-left px-2 py-1 font-normal">App</th>
                        <th className="text-left px-2 py-1 font-normal">Token type</th>
                        <th className="text-left px-2 py-1 font-normal">Client IP</th>
                        <th className="text-left px-2 py-1 font-normal">ASN</th>
                        <th className="text-left px-2 py-1 font-normal">Country</th>
                        <th className="text-left px-2 py-1 font-normal">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedEvents.map((ev, idx) => {
                        const eid = ev.eventId || `${idx}-${ev.time}`;
                        const isOpen = !!expanded[eid];
                        const nf = ev.newForUser || {};
                        // The AiTM tell: a successful token use from a new ASN or country.
                        const tell = ev.result === 'success' && (nf.asn || nf.country);
                        const rowTone = tell
                          ? 'bg-amber-950/30 hover:bg-amber-950/50'
                          : (idx % 2 ? 'bg-gray-900/30 hover:bg-gray-900/60' : 'hover:bg-gray-900/50');
                        return (
                          <React.Fragment key={eid}>
                            <tr
                              className={`border-t border-gray-800/60 cursor-pointer ${rowTone}`}
                              onClick={(e) => { e.stopPropagation(); setExpanded(p => ({ ...p, [eid]: !isOpen })); }}
                            >
                              <td className="px-1">{isOpen ? <ChevronDownIcon className="w-3 h-3 text-gray-500" /> : <ChevronRightIcon className="w-3 h-3 text-gray-500" />}</td>
                              <td className="px-2 py-1 font-mono text-gray-300 whitespace-nowrap">{fmtTs(ev.time, tzIana)}</td>
                              <td className="px-2 py-1 text-gray-200 truncate" style={{ maxWidth: 200 }} title={ev.app}>{ev.app || '—'}</td>
                              <td className="px-2 py-1 text-gray-400 whitespace-nowrap">{ev.tokenType || '—'}</td>
                              <td className="px-2 py-1 font-mono text-gray-300 whitespace-nowrap">
                                {ev.clientIp || '—'}{nf.ip ? <NewChip /> : null}
                                {ev.provider ? <span className="ml-1 text-gray-500">· {ev.provider}</span> : null}
                              </td>
                              <td className={`px-2 py-1 font-mono whitespace-nowrap ${nf.asn ? 'text-amber-300 font-semibold' : 'text-gray-400'}`}>
                                {ev.asn || '—'}{nf.asn ? <NewChip /> : null}
                              </td>
                              <td className={`px-2 py-1 whitespace-nowrap ${nf.country ? 'text-amber-300 font-semibold' : 'text-gray-400'}`}>
                                {ev.country || '—'}{nf.country ? <NewChip /> : null}
                              </td>
                              <td className="px-2 py-1"><ResultBadge result={ev.result} /></td>
                            </tr>
                            {isOpen && (
                              <tr className="bg-gray-950/80">
                                <td colSpan={8} className="px-4 py-2">
                                  <RawEventJsonPanel rawEventJson={ev.rawEventJson} title="Source event JSON" />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                      {sortedEvents.length === 0 && (
                        <tr><td colSpan={8} className="px-2 py-3 text-center text-gray-500 italic">No non-interactive sign-ins found in the window.</td></tr>
                      )}
                    </tbody>
                  </table>
                  {sortedEvents.length > 0 && (
                    <Pagination
                      currentPage={page}
                      totalPages={totalPages}
                      pageSize={pageSize}
                      totalItems={sortedEvents.length}
                      onPageChange={(p) => setPage(p)}
                      onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
                    />
                  )}
                </div>
              )}

              {tab === 'summary' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Counts */}
                  <div className="md:col-span-2 flex flex-wrap gap-3 text-[11px]">
                    <span className="text-gray-400">Token uses: <span className="text-gray-200 font-mono">{summary?.eventCount ?? 0}</span></span>
                    <span className="text-gray-400">Success: <span className="text-emerald-300 font-mono">{summary?.successCount ?? 0}</span></span>
                    <span className="text-gray-400">Failed: <span className="text-gray-300 font-mono">{summary?.failedCount ?? 0}</span></span>
                    <span className="text-gray-400">Distinct IPs: <span className="text-gray-200 font-mono">{summary?.distinctIpCount ?? 0}</span></span>
                    <span className="text-gray-400">New ASN (success): <span className={`font-mono ${summary?.foreignAsnCount ? 'text-amber-300' : 'text-gray-300'}`}>{summary?.foreignAsnCount ?? 0}</span></span>
                  </div>

                  {/* Originating interactive legs */}
                  <div className="md:col-span-2">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
                      Originating interactive sign-ins ({legs.length}) — stitched by correlationId
                    </p>
                    {legs.length ? (
                      <table className="w-full text-[11px] border border-gray-800 rounded">
                        <thead className="bg-gray-900/60 text-gray-400">
                          <tr>
                            <th className="text-left px-2 py-1 font-normal">Time</th>
                            <th className="text-left px-2 py-1 font-normal">Client IP</th>
                            <th className="text-left px-2 py-1 font-normal">Country</th>
                            <th className="text-left px-2 py-1 font-normal">App</th>
                            <th className="text-left px-2 py-1 font-normal">Result</th>
                          </tr>
                        </thead>
                        <tbody>
                          {legs.map((l, i) => (
                            <tr key={`${l.correlationId}-${i}`} className="border-t border-gray-800/60">
                              <td className="px-2 py-1 font-mono text-gray-300 whitespace-nowrap">{fmtTs(l.time, tzIana)}</td>
                              <td className="px-2 py-1 font-mono text-gray-300">{l.clientIp || '—'}</td>
                              <td className="px-2 py-1 text-gray-400">{l.country || '—'}</td>
                              <td className="px-2 py-1 text-gray-400 truncate" style={{ maxWidth: 200 }} title={l.app}>{l.app || '—'}</td>
                              <td className="px-2 py-1"><ResultBadge result={l.result === 'success' || l.result === 'failed' ? l.result : (l.result ? 'unknown' : 'unknown')} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : <p className="text-[11px] text-gray-500 italic">No interactive sign-in matched these tokens' correlationIds in the window.</p>}
                  </div>

                  {/* IPs touched */}
                  <div className="md:col-span-2">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">IPs touched ({ipSummary.length})</p>
                    {ipSummary.length ? (
                      <table className="w-full text-[11px] border border-gray-800 rounded">
                        <thead className="bg-gray-900/60 text-gray-400">
                          <tr>
                            <th className="text-left px-2 py-1 font-normal">IP</th>
                            <th className="text-left px-2 py-1 font-normal">Uses</th>
                            <th className="text-left px-2 py-1 font-normal">Provider</th>
                            <th className="text-left px-2 py-1 font-normal">Network</th>
                            <th className="text-left px-2 py-1 font-normal">Country</th>
                            <th className="text-left px-2 py-1 font-normal">Risk</th>
                            <th className="text-left px-2 py-1 font-normal">Flags</th>
                            <th className="text-left px-2 py-1 font-normal">Service tier</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ipSummary.map(ip => {
                            const flags = [
                              ip.detectionsVpn ? 'VPN' : null,
                              ip.detectionsProxy ? 'Proxy' : null,
                              ip.detectionsTor ? 'TOR' : null,
                              ip.detectionsAnon ? 'Anon' : null,
                            ].filter(Boolean);
                            return (
                              <tr key={ip.ip} className="border-t border-gray-800/60">
                                <td className="px-2 py-1 font-mono text-gray-300">
                                  <span className="group inline-flex items-center gap-1">
                                    {ip.ip}
                                    <CopyButton text={ip.ip} />
                                  </span>
                                </td>
                                <td className="px-2 py-1 font-mono text-gray-400">{ip.count != null ? ip.count : '—'}</td>
                                <td className="px-2 py-1 text-gray-400">{ip.provider || '—'}</td>
                                <td className="px-2 py-1 text-gray-400">{ip.networkType || '—'}</td>
                                <td className="px-2 py-1 text-gray-400">{ip.country || '—'}</td>
                                <td className="px-2 py-1 font-mono text-gray-400">{ip.riskLevel != null ? ip.riskLevel : '—'}</td>
                                <td className="px-2 py-1">
                                  {flags.length
                                    ? <span className="text-amber-300">{flags.join(' · ')}</span>
                                    : <span className="text-gray-600">—</span>}
                                </td>
                                <td className="px-2 py-1 text-gray-400">{ip.azureServiceTier || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : <p className="text-[11px] text-gray-500 italic">No IPs.</p>}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
